import React, { useCallback, useEffect, useState } from 'react';
import { getUrlDataExplore, getUrlDataExploreDetail } from '../../api/url_data';
import { getUrlDataLink } from '../../helpers/url_data_helpers';
import CatalogToolSubNav from '../CatalogTool/CatalogToolSubNav';
import './UrlDataExplore.scss';

const PAGE_SIZE = 50;

const readIdFromUrl = () => {
  const v = parseInt(new URLSearchParams(window.location.search).get('UrlData_id'), 10);
  return v > 0 ? v : null;
};

const setUrlId = id => {
  const params = new URLSearchParams(window.location.search);
  if (id) params.set('UrlData_id', String(id));
  else params.delete('UrlData_id');
  const qs = params.toString();
  window.history.pushState(null, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
};

const formatPrice = p => {
  if (p === null || p === undefined || p === '') return null;
  const s = String(p).trim();
  if (!s) return null;
  return /^[\d.]+$/.test(s) ? `$${s}` : s;
};

const formatDate = d => {
  if (!d) return null;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleString();
};

const hideBrokenImg = e => {
  e.target.style.display = 'none';
};

const Field = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className='ude-field'>
      <span className='ude-field-label'>{label}</span>
      <span className='ude-field-value'>{value}</span>
    </div>
  );
};

const Bool = ({ value }) => {
  if (value === null || value === undefined) return 'unknown';
  return value ? 'Yes' : 'No';
};

const UrlDataExplore = () => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState('newest');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState(() => readIdFromUrl());
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [findUrl, setFindUrl] = useState('');
  const [finding, setFinding] = useState(false);
  const [findError, setFindError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUrlDataExplore({ page, order, limit: PAGE_SIZE });
      setRows(res?.data?.rows || []);
    } catch (err) {
      setError(err || 'Failed to load URL data');
    } finally {
      setLoading(false);
    }
  }, [page, order]);

  useEffect(() => {
    load();
  }, [load]);

  // Fetch the active row whenever the selected id changes (click, find, or URL).
  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return undefined;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetail(null);
    getUrlDataExploreDetail(selected)
      .then(res => {
        if (!cancelled) setDetail(res?.data || null);
      })
      .catch(err => {
        if (!cancelled) setDetail({ error: err || 'Failed to load detail' });
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected]);

  // Keep state in sync with browser back/forward.
  useEffect(() => {
    const onPop = () => setSelected(readIdFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const goToDetail = id => {
    setUrlId(id);
    setSelected(id);
  };

  const closeDetail = () => {
    setUrlId(null);
    setSelected(null);
  };

  const onFind = async e => {
    e.preventDefault();
    const u = findUrl.trim();
    if (!u || finding) return;
    setFinding(true);
    setFindError(null);
    try {
      const res = await getUrlDataExplore({ url: u });
      const id = res?.data?.rows?.[0]?.id;
      if (id) goToDetail(id);
      else setFindError('No URL data found for that link');
    } catch (err) {
      setFindError(err || 'No URL data found for that link');
    } finally {
      setFinding(false);
    }
  };

  const renderDetail = () => {
    if (detailLoading) return <div className='ude-detail-empty'>Loading…</div>;
    if (!detail) return null;
    if (detail.error) return <div className='ude-error'>{detail.error}</div>;

    const { urlData = {}, scraperData, product } = detail;
    const scrape = scraperData?.extensionScraperData || null;
    const images = Array.isArray(scrape?.images) ? scrape.images.filter(Boolean) : [];
    const heroImage = images[0] || product?.image || urlData.primaryImageUrl || null;
    const title = scrape?.title || urlData.productName || product?.title || urlData.brandName || urlData.urlStem || `UrlData #${urlData.id}`;
    const brand = scrape?.brand || urlData.brandName || product?.brandName || product?.brand || null;
    const price = formatPrice(scrape?.price ?? urlData.productPrice);
    const link = getUrlDataLink(urlData);

    return (
      <div className='ude-detail-card'>
        <div className='ude-detail-top'>
          <span className='ude-status'>{urlData.status || 'unknown'}</span>
          <span className='ude-detail-id'>UrlData #{urlData.id}</span>
          <div className='ude-detail-actions'>
            {link && (
              <a href={link} target='_blank' rel='noreferrer'>
                Open link ↗
              </a>
            )}
            <button type='button' onClick={closeDetail}>
              Close
            </button>
          </div>
        </div>

        <div className='ude-hero'>
          <div className='ude-hero-img'>
            {heroImage ? <img src={heroImage} alt='' onError={hideBrokenImg} /> : <span className='ude-noimg'>No image</span>}
          </div>
          <div className='ude-hero-meta'>
            <h2>{title}</h2>
            <div className='ude-hero-sub'>{[brand, price, urlData.domain].filter(Boolean).join(' · ')}</div>
            <div className='ude-hero-stock'>
              In stock: <Bool value={urlData.isInStock} /> &nbsp;·&nbsp; Product page: <Bool value={urlData.isProductPage} />
            </div>
          </div>
        </div>

        <section className='ude-section'>
          <h3>From the extension</h3>
          {scrape ? (
            <>
              <div className='ude-fields'>
                <Field label='Last scraped' value={formatDate(scraperData.extensionSeenAt)} />
                <Field label='Source' value={scraperData.source || scrape.source} />
                <Field label='Extension version' value={scraperData.extensionVersion} />
                <Field label='Title' value={scrape.title} />
                <Field label='Brand' value={scrape.brand} />
                <Field label='Price' value={formatPrice(scrape.price)} />
              </div>
              {scrape.description && <p className='ude-desc'>{scrape.description}</p>}
              {images.length > 0 && (
                <div className='ude-thumbs'>
                  {images.slice(0, 8).map((src, i) => (
                    <img key={i} src={src} alt='' onError={hideBrokenImg} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className='ude-muted'>No extension scrape recorded for this URL yet.</p>
          )}
        </section>

        <section className='ude-section'>
          <h3>From our catalog / crawl</h3>
          <div className='ude-fields'>
            <Field label='Status' value={urlData.status} />
            <Field label='Brand' value={urlData.brandName} />
            <Field label='Product name' value={urlData.productName} />
            <Field label='Price' value={formatPrice(urlData.productPrice)} />
            <Field label='Currency' value={urlData.productCurrency} />
            <Field label='Crawl attempts' value={urlData.crawlAttempts} />
            <Field label='Last crawled' value={formatDate(urlData.lastCrawlAttemptAt)} />
            <Field label='Parsed at' value={formatDate(urlData.parsedAt)} />
            <Field label='First seen' value={formatDate(urlData.createdAt)} />
          </div>
          <Field label='URL stem' value={urlData.urlStem} />
        </section>

        <section className='ude-section'>
          <h3>Matched catalog product</h3>
          {product ? (
            <a className='ude-product' href={`/products/${product.id}`}>
              <div className='ude-product-img'>
                {product.image ? <img src={product.image} alt='' onError={hideBrokenImg} /> : <span className='ude-noimg'>—</span>}
              </div>
              <div>
                <div className='ude-product-title'>{product.title || product.name || `Product #${product.id}`}</div>
                {(product.brandName || product.brand) && <div className='ude-muted'>{product.brandName || product.brand}</div>}
                <div className='ude-link-text'>View product #{product.id} ↗</div>
              </div>
            </a>
          ) : (
            <p className='ude-muted'>Not matched to a catalog product yet.</p>
          )}
        </section>

        <details className='ude-raw'>
          <summary>Raw data</summary>
          <h4>UrlData</h4>
          <pre>{JSON.stringify(urlData, null, 2)}</pre>
          <h4>UrlScraperData</h4>
          <pre>{scraperData ? JSON.stringify(scraperData, null, 2) : 'none'}</pre>
          <h4>Product</h4>
          <pre>{product ? JSON.stringify(product, null, 2) : 'none'}</pre>
        </details>
      </div>
    );
  };

  return (
    <div className='url-data-explore'>
      <header className='ude-header'>
        <CatalogToolSubNav />
        <h1>URL Data Explorer</h1>
        <div className='ude-controls'>
          <select
            value={order}
            onChange={e => {
              setOrder(e.target.value);
              setPage(1);
            }}
          >
            <option value='newest'>Newest first</option>
            <option value='oldest'>Oldest first</option>
          </select>
          <div className='ude-pager'>
            <button disabled={page <= 1 || loading} onClick={() => setPage(p => Math.max(1, p - 1))}>
              Prev
            </button>
            <span>Page {page}</span>
            <button disabled={loading || rows.length < PAGE_SIZE} onClick={() => setPage(p => p + 1)}>
              Next
            </button>
          </div>
        </div>
      </header>

      <form className='ude-find' onSubmit={onFind}>
        <input type='text' value={findUrl} onChange={e => setFindUrl(e.target.value)} placeholder='Paste a product URL to find its UrlData row…' />
        <button type='submit' disabled={finding || !findUrl.trim()}>
          {finding ? 'Finding…' : 'Find'}
        </button>
        {findError && <span className='ude-find-error'>{findError}</span>}
      </form>

      {error && <div className='ude-error'>{error}</div>}

      <div className='ude-body'>
        <div className='ude-table-wrap'>
          <table className='ude-table'>
            <thead>
              <tr>
                <th>ID</th>
                <th>Link</th>
                <th>Domain</th>
                <th>Status</th>
                <th>Product</th>
                <th>Scrape</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6}>Loading…</td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6}>No rows.</td>
                </tr>
              )}
              {!loading &&
                rows.map(row => (
                  <tr key={row.id} className={selected === row.id ? 'is-selected' : ''} onClick={() => goToDetail(row.id)}>
                    <td>{row.id}</td>
                    <td className='ude-link'>
                      <a href={getUrlDataLink(row)} target='_blank' rel='noreferrer' onClick={e => e.stopPropagation()}>
                        {row.urlStem}
                      </a>
                    </td>
                    <td>{row.domain}</td>
                    <td>{row.status}</td>
                    <td>
                      {row.Product_id ? (
                        <a href={`/products/${row.Product_id}`} onClick={e => e.stopPropagation()}>
                          {row.productName || row.Product_id}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{row.extensionSeenAt ? `${row.scraperSource || 'scrape'} · ${new Date(row.extensionSeenAt).toLocaleDateString()}` : '—'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {selected && <div className='ude-detail'>{renderDetail()}</div>}
      </div>
    </div>
  );
};

export default UrlDataExplore;
